import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Building2,
  ClipboardCheck,
  CreditCard,
  FileText,
  Settings,
  ShieldCheck,
  ShieldAlert,
  Shuffle,
  MessageSquare,
  BarChart3,
  Grid3X3,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Search,
  Sun,
  Moon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { hasRouteAccess, ROLE_LABELS, type AdminRole, type Permission, hasAnyPermission } from '../../constants/rbac';
import { useCommandPalette } from '../../hooks/useCommandPalette';
import { useTheme } from '../../hooks/useTheme';
import CommandPalette from '../ui/CommandPalette';
import NotificationBell from './NotificationBell';
import type { User } from '../../types';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  allowedRoles?: readonly AdminRole[];
  requiredPermission?: Permission;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Occupancy Grid', href: '/occupancy', icon: Grid3X3, requiredPermission: 'occupancy:view' },
      { name: 'Reports', href: '/reports', icon: BarChart3, requiredPermission: 'reports:view' },
    ],
  },
  {
    label: 'Hostel Operations',
    items: [
      {
        name: 'Facilities & Balloting',
        href: '/facilities',
        icon: Building2,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN', 'STAFF'],
        requiredPermission: 'hostels:view',
      },
      {
        name: 'Booking Reviews',
        href: '/admin/booking-reviews',
        icon: ClipboardCheck,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN', 'STAFF'],
        requiredPermission: 'bookings:view',
      },
      {
        name: 'Allocations',
        href: '/admin/allocations',
        icon: Shuffle,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN', 'STAFF'],
        requiredPermission: 'allocations:view',
      },
    ],
  },
  {
    label: 'Finance',
    items: [
      {
        name: 'Payments',
        href: '/payments',
        icon: CreditCard,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN', 'STAFF'],
        requiredPermission: 'payments:view',
      },
      {
        name: 'Bank Statements',
        href: '/bank-statements',
        icon: FileText,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN', 'STAFF'],
        requiredPermission: 'bank-statements:view',
      },
    ],
  },
  {
    label: 'People',
    items: [
      {
        name: 'Students',
        href: '/students',
        icon: Users,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN', 'STAFF'],
        requiredPermission: 'students:view',
      },
      {
        name: 'Blacklist',
        href: '/blacklist',
        icon: ShieldAlert,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN'],
        requiredPermission: 'students:blacklist',
      },
      {
        name: 'Student Import',
        href: '/student-import',
        icon: UserPlus,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN'],
        requiredPermission: 'students:import',
      },
      {
        name: 'Support Inbox',
        href: '/support-tickets',
        icon: MessageSquare,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN', 'STAFF'],
        requiredPermission: 'support:view',
      },
    ],
  },
  {
    label: 'System',
    items: [
      {
        name: 'Settings',
        href: '/settings',
        icon: Settings,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN'],
        requiredPermission: 'settings:view',
      },
      {
        name: 'Team Access',
        href: '/admin/staff-access',
        icon: ShieldCheck,
        allowedRoles: ['ADMIN', 'SUPER_ADMIN'],
        requiredPermission: 'staff:view',
      },
    ],
  },
];

const defaultAllowedRoles: readonly AdminRole[] = ['ADMIN', 'SUPER_ADMIN', 'STAFF'];

const withDefaultRoles = (groups: NavGroup[]): NavGroup[] =>
  groups.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      allowedRoles: item.allowedRoles ?? defaultAllowedRoles,
    })),
  }));

const resolvedNavGroups = withDefaultRoles(navGroups);

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const commandPalette = useCommandPalette();
  const { resolvedTheme, toggle: toggleTheme } = useTheme();

  const sidebarWidth = collapsed ? 'w-[68px]' : 'w-64';

  const initials = [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('').toUpperCase() || user?.email?.[0]?.toUpperCase() || '?';

  return (
    <div className="min-h-screen admin-shell">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-foreground focus:shadow-lg focus:ring-2 focus:ring-ring">
        Skip to main content
      </a>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-foreground/40 backdrop-blur-sm animate-fade-fast"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 w-64 bg-background shadow-xl animate-slide-right">
            <SidebarHeader collapsed={false} />
            <SidebarNav
              location={location}
              collapsed={false}
              userRole={user?.role}
              onNavigate={() => setSidebarOpen(false)}
            />
            <SidebarFooter user={user} initials={initials} collapsed={false} onLogout={logout} />
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:flex-col ${sidebarWidth} border-r border-border bg-background transition-all duration-200 z-30`}
      >
        <SidebarHeader collapsed={collapsed} />
        <SidebarNav location={location} collapsed={collapsed} userRole={user?.role} />
        <SidebarFooter user={user} initials={initials} collapsed={collapsed} onLogout={logout} />

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 z-40 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm hover:text-foreground transition-colors"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-200 ${collapsed ? 'lg:pl-[68px]' : 'lg:pl-64'}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-20 border-b border-border/80 bg-background/80 backdrop-blur-md">
          <div className="flex h-14 items-center justify-between px-4 lg:px-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground lg:hidden lg:h-auto lg:w-auto lg:p-1.5 transition-colors"
                aria-label="Open navigation menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <Breadcrumb location={location} />
            </div>

            <div className="flex items-center gap-3">
              {/* Command palette trigger */}
              <button
                onClick={commandPalette.toggle}
                className="hidden sm:flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                aria-label="Open command palette"
              >
                <Search className="h-3.5 w-3.5" />
                <span className="text-xs">Search...</span>
                <kbd className="ml-2 inline-flex h-5 items-center gap-0.5 rounded border border-border bg-background px-1.5 text-[10px] font-medium">
                  &#8984;K
                </kbd>
              </button>
              <button
                onClick={commandPalette.toggle}
                className="sm:hidden flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                aria-label="Open command palette"
              >
                <Search className="h-5 w-5" />
              </button>

              <NotificationBell />

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="flex h-11 w-11 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors sm:h-auto sm:w-auto sm:p-1.5"
                aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                title={resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}
              >
                {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>

              <div className="hidden sm:flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white">
                  {initials}
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground leading-tight">
                    {user?.firstName || user?.email}
                  </p>
                  <p className="text-overline uppercase text-muted-foreground">
                    {user?.role ? ROLE_LABELS[user.role] : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={logout}
                className="flex h-11 w-11 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors sm:h-auto sm:w-auto sm:p-1.5"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </header>

        <main id="main-content" className="p-4 lg:p-6">{children}</main>
      </div>

      <CommandPalette open={commandPalette.open} onClose={commandPalette.close} />
    </div>
  );
};

/* ─── Sub-components ─────────────────────── */

const SidebarHeader: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <div className="flex h-14 items-center border-b border-border px-4">
    <div className="flex items-center gap-2.5 overflow-hidden">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand text-xs font-bold text-white">
        H
      </div>
      {!collapsed && (
        <span className="text-sm font-bold text-foreground whitespace-nowrap">Hostel Admin</span>
      )}
    </div>
  </div>
);

interface SidebarNavProps {
  location: { pathname: string };
  collapsed: boolean;
  userRole?: User['role'];
  onNavigate?: () => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({ location, collapsed, userRole, onNavigate }) => (
  <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
    {resolvedNavGroups.map((group) => {
      const visibleItems = group.items.filter((item) => {
        const roleOk = hasRouteAccess(userRole, item.allowedRoles ?? defaultAllowedRoles);
        if (!roleOk) return false;
        if (item.requiredPermission) {
          return hasAnyPermission(userRole, [item.requiredPermission]);
        }
        return true;
      });

      if (visibleItems.length === 0) {
        return null;
      }

      return (
      <div key={group.label}>
        {!collapsed && (
          <p className="mb-2 px-2 text-overline uppercase text-muted-foreground">
            {group.label}
          </p>
        )}
        <div className="space-y-0.5">
          {visibleItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onNavigate}
                title={collapsed ? item.name : undefined}
                className={`flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-brand text-white shadow-sm'
                    : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:translate-x-0.5'
                } ${collapsed ? 'justify-center px-2' : ''}`}
              >
                <item.icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? 'text-white' : 'text-muted-foreground'}`} />
                {!collapsed && <span className="truncate">{item.name}</span>}
              </Link>
            );
          })}
        </div>
      </div>
    )})}
  </nav>
);

interface SidebarFooterProps {
  user: any;
  initials: string;
  collapsed: boolean;
  onLogout: () => void;
}

const SidebarFooter: React.FC<SidebarFooterProps> = ({ user, initials, collapsed, onLogout }) => (
  <div className="border-t border-border p-3">
    <div className={`flex items-center gap-2.5 ${collapsed ? 'justify-center' : ''}`}>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white">
        {initials}
      </div>
      {!collapsed && (
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {user?.firstName || user?.email}
          </p>
          <p className="truncate text-overline text-muted-foreground">{user?.email}</p>
        </div>
      )}
    </div>
  </div>
);

const Breadcrumb: React.FC<{ location: { pathname: string } }> = ({ location }) => {
  const pathMap: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/reports': 'Reports & Analytics',
    '/occupancy': 'Occupancy Grid',
    '/students': 'Students',
    '/student-import': 'Student Import',
    '/support-tickets': 'Support Inbox',
    '/facilities': 'Facilities & Balloting',
    '/admin/booking-reviews': 'Booking Reviews',
    '/admin/allocations': 'Allocations',
    '/admin/allocations/history': 'Allocation History',
    '/admin/allocations/rules': 'Allocation Rules',
    '/admin/allocations/preview': 'Results Preview',
    '/admin/hostels': 'Hostel Portfolio',
    '/payments': 'Payments',
    '/bank-statements': 'Bank Statements',
    '/settings': 'Settings',
    '/admin/staff-access': 'Team Access',
  };

  const current = pathMap[location.pathname] || 'Page';

  return (
    <div className="hidden lg:flex items-center gap-1.5 text-sm">
      <span className="text-muted-foreground">Admin</span>
      <ChevronRight className="h-3.5 w-3.5 text-border" />
      <span className="font-medium text-foreground">{current}</span>
    </div>
  );
};

export default Layout;
